import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, Download, Trash2, FileImage, FileText, Eye } from "lucide-react";
import {
  listClaimDocuments,
  uploadClaimDocument,
  deleteClaimDocument,
  fetchClaimDocumentBlob,
  listProcedureDocuments,
  uploadProcedureDocument,
  deleteProcedureDocument,
  fetchProcedureDocumentBlob,
  triggerBrowserDownload,
  DocumentResponse,
} from "@/services/insurance-authorization-service";

export type DocScope = "claim" | "procedure";

function isImage(mime?: string): boolean {
  return !!mime && mime.startsWith("image/");
}

function formatSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentList({
  scope,
  parentId,
  refreshKey,
  onChanged,
}: {
  scope: DocScope;
  parentId: string;
  refreshKey: number;
  onChanged: () => void;
}) {
  const [docs, setDocs] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{ url: string; filename: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DocumentResponse | null>(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = scope === "claim"
        ? await listClaimDocuments(parentId)
        : await listProcedureDocuments(parentId);
      setDocs(items);
    } catch (error) {
      toast({
        title: "Erro ao carregar documentos",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [scope, parentId, toast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleView = async (doc: DocumentResponse) => {
    try {
      const blob = scope === "claim"
        ? await fetchClaimDocumentBlob(doc.id)
        : await fetchProcedureDocumentBlob(doc.id);
      const url = URL.createObjectURL(blob);
      setPreview({ url, filename: doc.original_filename || "documento" });
    } catch (error) {
      toast({
        title: "Erro ao visualizar",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (doc: DocumentResponse) => {
    try {
      const blob = scope === "claim"
        ? await fetchClaimDocumentBlob(doc.id)
        : await fetchProcedureDocumentBlob(doc.id);
      triggerBrowserDownload(blob, doc.original_filename || `documento-${doc.id}`);
    } catch (error) {
      toast({
        title: "Erro ao baixar",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const doc = pendingDelete;
    setPendingDelete(null);
    try {
      if (scope === "claim") await deleteClaimDocument(doc.id);
      else await deleteProcedureDocument(doc.id);
      toast({ title: "Documento excluído." });
      await load();
      onChanged();
    } catch (error) {
      toast({
        title: "Erro ao excluir",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  if (loading && docs.length === 0) {
    return <p className="text-[10px] text-muted-foreground">Carregando...</p>;
  }

  if (docs.length === 0) {
    return <p className="text-[10px] text-muted-foreground">Nenhum documento.</p>;
  }

  return (
    <>
      <ul className="space-y-1">
        {docs.map((doc) => (
          <li key={doc.id} className="flex items-center gap-2 text-[11px] bg-background border border-border rounded px-2 py-1">
            {isImage(doc.mime_type) ? <FileImage className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            <span className="truncate flex-1">{doc.original_filename || "documento"}</span>
            <span className="text-muted-foreground text-[10px] shrink-0">{formatSize(doc.size_bytes)}</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Visualizar" onClick={() => handleView(doc)}>
              <Eye className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Baixar" onClick={() => handleDownload(doc)}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" title="Excluir" onClick={() => setPendingDelete(doc)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={!!preview} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">{preview?.filename}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="max-h-[70vh] overflow-auto flex items-center justify-center">
              {/\.(jpe?g|png|webp|gif)$/i.test(preview.filename) ? (
                <img src={preview.url} alt={preview.filename} className="max-w-full h-auto" />
              ) : (
                <iframe src={preview.url} title={preview.filename} className="w-full h-[70vh]" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="max-w-[420px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir documento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{pendingDelete?.original_filename || "este documento"}</strong>?
              <br /><br />
              <span className="text-xs text-muted-foreground">Esta ação não pode ser desfeita.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function DocumentUploader({
  scope,
  parentId,
  onUploaded,
}: {
  scope: DocScope;
  parentId: string;
  onUploaded: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      if (scope === "claim") await uploadClaimDocument(parentId, file);
      else await uploadProcedureDocument(parentId, file);
      toast({ title: "Documento enviado!" });
      onUploaded();
    } catch (error) {
      toast({
        title: "Erro ao enviar",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <label className="inline-flex items-center gap-1 cursor-pointer text-[11px] px-2 py-1 rounded border border-dashed border-border hover:bg-muted/50">
      <Upload className="h-3 w-3" />
      {uploading ? "Enviando..." : "Anexar"}
      <input
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={handleChange}
        disabled={uploading}
      />
    </label>
  );
}
